import { useMemo, useState, useEffect } from 'react';
import loadable from '@loadable/component';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import { Button, Row, Column, Alert, Text, Box } from '@simplywallst/ui-core';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import ReportSection from '@/components/ReportSection';
import ReportSubSection, {
  SubSectionTitle,
} from '@/components/ReportSubSection';
import track, { useTracking } from 'react-tracking';
import {
  COMPANY_EARNINGS_HISTORY,
  COMPANY_PAST_GROWTH_ANALYSIS,
  COMPANY_PAST_ROA,
  COMPANY_PAST_ROCE,
  COMPANY_PAST_ROE,
} from '@/constants/feedbackSections';
import {
  ChartType,
  COMPANY_PAST_PERFORMANCE,
} from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@/utilities/urlGenerator';
import {
  LegacyAnchor,
  PAST_EARNINGS_GROWTH,
  PAST_EARNINGS_REVENUE,
  PAST_ROA,
  PAST_ROE,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import type { StatementName } from '@/hooks/useCompanyStatementsAPIFormatted/types';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getStatementChecks } from '@/pages/CompanyReport/components/Introduction/utils';
import { EarningsAndRevenueHistoryTable } from '../EarningsAndRevenueHistory/components/EarningsAndRevenueHistoryTable/EarningsAndRevenueHistoryTable';
import { SectionSchema } from '@/pages/CompanyReport/schemas';
import useExperimentPeerCompanies from '@/pages/CompanyReport/components/SimilarCompanies/useExperimentPeerCompanies';
import { useCompany } from '@/hooks/useCompany';
import FreeCashFlow from '../FreeCashFlow';

const PastHeader = loadable(() => import('../PastHeader'));

const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const ExtendedIncomeStatement = loadable(
  () => import('../ExtendedIncomeStatement')
);

const ROCEGauge = loadable(() => import('../ROCEGauge'), { ssr: false });
const ROAGauge = loadable(() => import('../ROAGauge'), { ssr: false });
const ROEGauge = loadable(() => import('../ROEGauge'), { ssr: false });
const PastNextStepsContainer = loadable(
  () => import('@/pages/CompanyReport/components/Past'),
  {
    resolveComponent: (component) => component.PastNextStepsContainer,
  }
);
const PastGrowth = loadable(
  () => import('@/pages/CompanyReport/components/Past'),
  {
    ssr: false,
    resolveComponent: (component) => component.PastGrowth,
  }
);

const EarningsAndRevenueHistory = loadable(
  () => import('../EarningsAndRevenueHistory'),
  { ssr: false }
);

export interface PastProps {
  companyId: string;
  isFund: boolean;
  keyMetric: string;
  keyMetricTitle: string;
  pastROE: number;
  pastROA: number;
  pastROCE1y: number;
  pastROCE3y: number;
  netIncomeGrowth1y: number | null;
  companyName: string;
  sectionNumber?: number;
  netIncomeGrowth5y: number | null;
  pastIndustryROE: number;
  pastIndustryROA: number;
  marketName: string;
  industryName: string;
  averageMarketGrowth1Y: number;
  averageMarketGrowth5Y: number;
  averageIndustryGrowth1Y: number;
  averageIndustryGrowth5Y: number;
  netIncomeGrowth5YFormatted: string;
  averageIndustryGrowth5YFormatted: string;
  averageMarketGrowth5YFormatted: string;
  netIncomeGrowth1YFormatted: string;
  averageIndustryGrowth1YFormatted: string;
  averageMarketGrowth1YFormatted: string;
}

const Past = ({
  netIncomeGrowth1y,
  netIncomeGrowth5y,
  averageIndustryGrowth1Y,
  averageIndustryGrowth5Y,
  averageMarketGrowth1Y,
  averageMarketGrowth5Y,
  companyId,
  companyName,
  industryName,
  isFund,
  marketName,
  pastIndustryROA,
  pastIndustryROE,
  pastROA,
  pastROCE1y,
  pastROCE3y,
  pastROE,
  sectionNumber,
  netIncomeGrowth5YFormatted,
  averageIndustryGrowth5YFormatted,
  averageMarketGrowth5YFormatted,
  netIncomeGrowth1YFormatted,
  averageIndustryGrowth1YFormatted,
  averageMarketGrowth1YFormatted,
}: PastProps) => {
  const { isPristine } = useVisibleSectionsContext();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const [showReport, setShowReport] = useState(true);
  const tracking = useTracking();
  const trackingButtonData = {
    action: 'click',
    modifier: 'show_Meaningless_data',
  };

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: company } = useCompany(canonicalUrl);
  const isFilingProcessed =
    company?.data.analysis?.data.extended.data.analysis.past
      .latest_filing_processed;
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const {
    HasHighQualityPastEarnings: hasHighQualityPastEarnings,
    HasPastNetProfitMarginImprovedOverLastYear:
      hasPastNetProfitMarginImprovedOverLastYear,
    HasGrownProfitsOverPast5Years: hasGrownProfitsOverPast5Years,
    HasProfitGrowthAccelerated: hasProfitGrowthAccelerated,
    IsGrowingFasterThanIndustry: isGrowingFasterThanIndustry,
    IsReturnOnEquityAboveThreshold: isReturnOnEquityAboveThreshold,
    IsPastAnalysisMeaningless: isPastAnalysisMeaningless,
  } = statements || {};

  const pastStatements: StatementName[] = [
    'HasHighQualityPastEarnings',
    'HasPastNetProfitMarginImprovedOverLastYear',
    'HasGrownProfitsOverPast5Years',
    'HasProfitGrowthAccelerated',
    'IsGrowingFasterThanIndustry',
    'IsReturnOnEquityAboveThreshold',
  ];
  const statementChecks = getStatementChecks(pastStatements, statements);

  const isDataMeaningless = isPastAnalysisMeaningless?.value === true;

  useEffect(() => {
    setShowReport(isDataMeaningless === false);
  }, [isDataMeaningless]);

  const {
    pastEarningsGrowthDownloadUrl,
    incomeStatementDownloadUrl,
    ROEDownloadUrl,
    ROADownloadUrl,
    ROCEDownloadUrl,
  } = useMemo(() => {
    return {
      pastEarningsGrowthDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.PastEarningsGrowth
      ),
      incomeStatementDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.EarningsAndRevenueHistory
      ),
      ROEDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.ROE
      ),
      ROADownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        COMPANY_PAST_PERFORMANCE
      ),
      ROCEDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.ROCE
      ),
    };
  }, [companyId]);

  const nextStep = useExperimentPeerCompanies({
    originalElement: (
      <PastNextStepsContainer
        companyId={companyId}
        isDataMeaningless={isDataMeaningless}
      />
    ),
    section: 'past',
  });

  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="past-section">
      <SectionSchema section="past" />
      <CompanySectionTitle title={'Past Earnings Performance'} />
      {isFilingProcessed === false && (
        <Box marginY="x2">
          <Alert color="phantom" icon={<InformationSolid fill="white" />}>
            <Text typography="xSmall">
              We&apos;re still processing the latest earnings report of this
              company
            </Text>
          </Alert>
        </Box>
      )}
      <PastHeader
        statementChecks={statementChecks}
        canonicalUrl={canonicalUrl}
        industryName={industryName}
      />
      {isDataMeaningless && (
        <ReportSubSection
          body={
            <>
              <p>
                {`In this section we usually display a company’s past earnings and revenues to help investors visualise the trend through time. We also gauge the company’s performance by benchmarking its returns and growth to its industry peers and the wider market. However, ${companyName} has not provided sufficient past data to assess its track record.`}
              </p>
              <p>
                This is quite a rare situation as 97% of companies covered by
                Simply Wall St do have historical data.
              </p>
              {showReport === false && (
                <Button
                  styleType="louderLarge"
                  onClick={() => {
                    tracking.trackEvent(trackingButtonData);
                    setShowReport(!showReport);
                  }}
                >
                  Show section
                </Button>
              )}
            </>
          }
        />
      )}

      {showReport && (
        <>
          <BrazeContentCardWrapper>
            <BrazeContentCard
              feedType="company-overview.past_performance"
              anchorTarget="_blank"
            />
          </BrazeContentCardWrapper>
          <PageAnchor type="sub-section-title" id={PAST_EARNINGS_REVENUE} />
          <ExtendedIncomeStatement companyName={companyName} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Earnings and Revenue History"
            trackingProps={{ subject: 'chart' }}
            bodyRender={() => (
              <ChartWrapper
                id="past-earnings-revenue-history-chart"
                chart={<EarningsAndRevenueHistory companyId={companyId} />}
                table={<EarningsAndRevenueHistoryTable />}
                feedbackLabel={COMPANY_EARNINGS_HISTORY}
                exportDarkUrl={incomeStatementDownloadUrl}
                preRenderHeightSmDown="475px"
                preRenderHeight="543px"
              />
            )}
            statements={
              <>
                <Statement
                  title={hasHighQualityPastEarnings?.title}
                  description={
                    <Description statement={hasHighQualityPastEarnings} />
                  }
                  value={hasHighQualityPastEarnings?.value}
                  statementId={CompanyStatements.pastStatement5}
                />
                <Statement
                  title={hasPastNetProfitMarginImprovedOverLastYear?.title}
                  description={
                    <Description
                      statement={hasPastNetProfitMarginImprovedOverLastYear}
                    />
                  }
                  value={hasPastNetProfitMarginImprovedOverLastYear?.value}
                  statementId={CompanyStatements.pastStatement6}
                />
              </>
            }
          />
          <hr />
          <FreeCashFlow />
          <hr />
          <PageAnchor type="sub-section-title" id={PAST_EARNINGS_GROWTH} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Past Earnings Growth Analysis"
            trackingProps={{ subject: 'chart' }}
            body={
              <ChartWrapper
                id="past-earnings-growth-analysis-chart"
                preRenderHeight="358px"
                preRenderHeightSmDown="654px"
                preRenderHeightMdDown="355px"
                chart={
                  <PastGrowth
                    companyName={companyName}
                    industryName={industryName}
                    marketName={marketName}
                    netIncomeGrowth5Y={netIncomeGrowth5y}
                    netIncomeGrowth1Y={netIncomeGrowth1y}
                    averageMarketGrowth1Y={averageMarketGrowth1Y}
                    averageMarketGrowth5Y={averageMarketGrowth5Y}
                    averageIndustryGrowth1Y={averageIndustryGrowth1Y}
                    averageIndustryGrowth5Y={averageIndustryGrowth5Y}
                    netIncomeGrowth5YFormatted={netIncomeGrowth5YFormatted}
                    averageIndustryGrowth5YFormatted={
                      averageIndustryGrowth5YFormatted
                    }
                    averageMarketGrowth5YFormatted={
                      averageMarketGrowth5YFormatted
                    }
                    netIncomeGrowth1YFormatted={netIncomeGrowth1YFormatted}
                    averageIndustryGrowth1YFormatted={
                      averageIndustryGrowth1YFormatted
                    }
                    averageMarketGrowth1YFormatted={
                      averageMarketGrowth1YFormatted
                    }
                  />
                }
                feedbackLabel={COMPANY_PAST_GROWTH_ANALYSIS}
                exportDarkUrl={pastEarningsGrowthDownloadUrl}
              />
            }
            statements={
              <>
                <Statement
                  title={hasGrownProfitsOverPast5Years?.title}
                  description={
                    <Description statement={hasGrownProfitsOverPast5Years} />
                  }
                  value={hasGrownProfitsOverPast5Years?.value}
                  statementId={CompanyStatements.pastStatement1}
                />
                <Statement
                  title={hasProfitGrowthAccelerated?.title}
                  description={
                    <Description statement={hasProfitGrowthAccelerated} />
                  }
                  value={hasProfitGrowthAccelerated?.value}
                  statementId={CompanyStatements.pastStatement2}
                />
                <Statement
                  title={isGrowingFasterThanIndustry?.title}
                  description={
                    <Description statement={isGrowingFasterThanIndustry} />
                  }
                  value={isGrowingFasterThanIndustry?.value}
                  statementId={CompanyStatements.pastStatement3}
                />
              </>
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={PAST_ROE} />
          <PageAnchor type="sub-section-title" id={LegacyAnchor.LAST_PERF} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Return on Equity"
            trackingProps={{ subject: 'chart' }}
            split={true}
            body={
              <ChartWrapper
                id="past-return-on-equity-chart"
                preRenderHeight="335px"
                preRenderHeightMdDown="327px"
                chart={
                  <ROEGauge
                    companyROE={pastROE}
                    companyName={companyName}
                    industryROE={pastIndustryROE}
                    industryName={industryName}
                  />
                }
                feedbackLabel={COMPANY_PAST_ROE}
                exportDarkUrl={ROEDownloadUrl}
              />
            }
            statements={
              <Statement
                title={isReturnOnEquityAboveThreshold?.title}
                description={
                  <Description statement={isReturnOnEquityAboveThreshold} />
                }
                value={isReturnOnEquityAboveThreshold?.value}
                statementId={CompanyStatements.pastStatement4}
              />
            }
          />
          <PageAnchor type="sub-section-title" id={PAST_ROA} />
          <PageAnchor type="sub-section-title" id={LegacyAnchor.PAST_ROCE} />
          <ReportSubSection
            trackingProps={{ subject: 'chart' }}
            body={
              <Row>
                <Column col={12} colLg={6}>
                  <hr />
                  <SubSectionTitle
                    as={subSectionTitleElement}
                    data-cy-id="report-sub-section-title-return-on-assets"
                  >
                    Return on Assets
                  </SubSectionTitle>
                  <ChartWrapper
                    id="past-return-on-assets-chart"
                    preRenderHeight="335px"
                    preRenderHeightMdDown="327px"
                    chart={
                      <ROAGauge
                        companyROA={pastROA}
                        companyName={companyName}
                        industryROA={pastIndustryROA}
                        industryName={industryName}
                      />
                    }
                    feedbackLabel={COMPANY_PAST_ROA}
                    exportDarkUrl={ROADownloadUrl}
                  />
                </Column>
                <Column col={12} colLg={6}>
                  <hr />
                  <SubSectionTitle
                    as={subSectionTitleElement}
                    data-cy-id="report-sub-section-title-return-on-capital-employed"
                  >
                    Return on Capital Employed
                  </SubSectionTitle>
                  <ChartWrapper
                    id="past-return-capital-employed-chart"
                    preRenderHeight="335px"
                    preRenderHeightMdDown="327px"
                    chart={
                      <ROCEGauge
                        companyName={companyName}
                        ROCE1y={pastROCE1y}
                        ROCE3y={pastROCE3y}
                      />
                    }
                    feedbackLabel={COMPANY_PAST_ROCE}
                    exportDarkUrl={ROCEDownloadUrl}
                  />
                </Column>
              </Row>
            }
          />
        </>
      )}
      {isFund === false && (
        <>
          <hr />
          {nextStep}
        </>
      )}
    </ReportSection>
  );
};

export default track({ type: 'track', subject: 'Past' })(Past);
